import * as React from 'react';
import { FC, ReactNode, useState } from 'react';
import moment from 'moment';
import { Day, TICKET_TYPES, TicketType, Ticket as TicketT, Prize as PrizeT } from './types';
import locales from './locales';
import { Popup } from './Popup';
import availableTicket from './images/ticket_available.png';
import defaultTicket from './images/ticket.png';

const groupDays = (days: Day[]): [Day, Day[]] | [null, Day[]] =>
  days[0]?.is_today ? [days[0], days.slice(1)] : [null, days];

const findTicket = (tickets: TicketT[], type: TicketType) => tickets.find((ticket) => ticket.type === type);

const NotAvailableTicket: FC<{ text: string }> = ({ text }) => (
  <div className="ticket_not-available t-na ticket-image-block">
    <div className="ticket-image t-na_image">
      <img src={defaultTicket} alt="ticket default" />
      <div className="t-na_text">{text}</div>
    </div>
  </div>
);

const AvailableTicket: FC = () => (
  <div className="ticket_available t-a ticket-image-block">
    <div className="ticket-image t-a_image">
      <img src={availableTicket} alt="ticket available" />
    </div>
  </div>
);

const Prize: FC<{ prize: PrizeT }> = ({ prize }) => (
  <div className="ticket_prize t-p ticket-image-block">
    <div className="t-p_image">
      <img src={prize.image.file_url || ''} alt={prize.wheel_text} />
    </div>
    <div className="t-p_text">{prize.name}</div>
  </div>
);

const Ticket: FC<{ ticket: TicketT }> = ({ ticket }) =>
  ticket.prize ? <Prize prize={ticket.prize} /> : <AvailableTicket />;

const TimeRow: FC<{
  dateText: string;
}> = ({ dateText }) => (
  <h3 className="tp-days_time">
    <span>{dateText}</span>
  </h3>
);

const DayRow: FC<{
  tickets: TicketT[];
  cellRenderer: (ticket: TicketT | undefined, type: TicketType) => ReactNode;
}> = ({ tickets, cellRenderer }) => (
  <ul>
    {TICKET_TYPES.map((type) => (
      <li key={type}>{cellRenderer(findTicket(tickets, type), type)}</li>
    ))}
  </ul>
);

const Today: FC<{ day: Day }> = ({ day }) => {
  const l = locales();
  const [popupInfos, setPopupInfos] = useState<{
    visible: boolean;
    text: string;
    type: string;
  }>({
    visible: false,
    text: '',
    type: 'casino_wager',
  });

  const changePopupText = (text: string, type: string) => {
    setPopupInfos({ visible: true, text, type });
  };

  const resetPopup = () => {
    setPopupInfos({ visible: false, text: '', type: 'casino_wager' });
  };

  return (
    <div className="tickets-today t-today">
      <h3 className="t-today_title">
        <span>{l.today}</span>
      </h3>
      <div className="t-today_list">
        <DayRow
          tickets={day.tickets}
          cellRenderer={(ticket, type) => {
            if (ticket)
              return (
                <div className="ticket">
                  <Ticket ticket={ticket} />
                  <div className="ticket_availability">
                    <div className="ticket_text">{ticket.prize ? l.earned : l.available}</div>
                  </div>
                </div>
              );

            const ticketText = l.ticket_rules[type];

            if (!ticketText || ticketText.length < 15)
              return (
                <div className="ticket">
                  <NotAvailableTicket text={l[type] as string} />
                  <div className="ticket_availability" style={{ textAlign: 'center' }}>
                    <div className="ticket_text">{l.ticket_not_received}</div>
                  </div>
                </div>
              );

            return (
              <div className="ticket">
                <NotAvailableTicket text={l[type] as string} />
                <div className="ticket_availability" style={{ textAlign: 'center' }}>
                  <button className="ticket_button" type="button" onClick={() => changePopupText(ticketText, type)}>
                    {l.get_ticket}
                  </button>
                </div>
              </div>
            );
          }}
        />
      </div>
      <Popup
        type="info"
        title={l[`ticket_info_${popupInfos.type}`] as string}
        visible={popupInfos.visible}
        render={() => (
          <>
            {/* eslint-disable-next-line react/no-danger */}
            <div
              className="td-popup_content"
              aria-label="get ticket info"
              dangerouslySetInnerHTML={{ __html: popupInfos.text }}
            />
          </>
        )}
        reset={resetPopup}
      />
    </div>
  );
};

const PastDays: FC<{ days: Day[] }> = ({ days }) => {
  const l = locales();

  return (
    <div className="tickets-past t-past">
      <div className="t-past_head t-head">
        <ul className="t-head_list">
          <li className="t-head_info-block" />
          {TICKET_TYPES.map((type) => (
            <li className="t-head_item" key={type}>
              <span>{l[type]}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="t-past_body">
        {days.map((day) => (
          <div className="t-past_days tp-days" key={day.date}>
            <TimeRow dateText={moment(day.date).format('DD.MM')} />
            <DayRow
              key={day.date}
              tickets={day.tickets}
              cellRenderer={(ticket) => (ticket ? <Ticket ticket={ticket} /> : null)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const Days: FC<{ days: Day[]; showPastDays: boolean }> = ({ days, showPastDays }) => {
  const l = locales();
  const [today, pastDays] = groupDays(days);
  const daysLabelId = 'lottery-days-label';

  return (
    <div className="l-w_days l-d" aria-labelledby={daysLabelId}>
      <div className="l-d_top">
        <h2 className="l-d_title" id={daysLabelId}>
          {l.my_tickets}
        </h2>
        <p className="l-d_text">{l.complete_tasks}</p>
      </div>
      {today && <Today day={today} />}
      {showPastDays && <PastDays days={pastDays} />}
    </div>
  );
};

export default Days;
